import { ChartDataResponse, DimensionInfo, MetricInfo } from "@api/common";
import * as cn from "classnames";
import * as React from "react";
import "./MetricsTable.css";
import { MetricLabels } from "@api/common";
import { formatNumber } from "@common";

export interface MetricsTableProps {
    data: ChartDataResponse;
    metrics: MetricInfo[];
    dimensions: DimensionInfo[];
    percentMetric: MetricInfo;
    className?: string;

    labelRender?: (dimension: DimensionInfo) => React.ReactChild;
}

export function MetricsTable({ data, dimensions, metrics, percentMetric, className, labelRender }: MetricsTableProps) {
    labelRender = labelRender || defaultLabelRender;
    const percent = calculateDimensionPercents(data, percentMetric, dimensions);

    return (
        <div className={cn("MetricsTable", className)}>
            <div className="MetricsTable__column" key="DimensionColumn">
                {dimensions.map(dim => (
                    <div className="MetricsTable__cell label" key={dim.dimension}>
                        {labelRender(dim)}
                    </div>
                ))}
            </div>
            <div className="MetricsTable__column" key="PercentColumn">
                <div className="MetricsTable__cell header" key="PercentMetricHeader">
                    {MetricLabels.relativeValueTableColumnHeader(percentMetric)}
                </div>
                {dimensions.map(dim => {
                    const p = percent.find(p => p.dimension === dim.dimension);
                    return (
                        <div className="MetricsTable__cell value" key={p.dimension}>{`${p.percent.toFixed(2)}%`}</div>
                    );
                })}
            </div>
            {metrics.map(metric => (
                <div className="MetricsTable__column" key={metric.metric}>
                    <div className="MetricsTable__cell header" key="MetricHeader">
                        {MetricLabels.tableColumnHeader(metric)}
                    </div>
                    {dimensions.map(dimension => (
                        <div className="MetricsTable__cell value" key={dimension.dimension}>
                            {formatValue(data, metric, dimension)}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function defaultLabelRender(dimension: DimensionInfo) {
    return (
        <>
            <span className="MetricsTable__metric-color" style={{ backgroundColor: dimension.color }}>
                &nbsp;
            </span>
            <span>{dimension.title || dimension.dimension}</span>
        </>
    );
}

function formatValue(data: ChartDataResponse, metric: MetricInfo, dimension: DimensionInfo): string {
    const metricData = data.metrics.find(m => m.metric === metric.metric);

    if (!metricData) {
        return "-";
    }

    const dataItem = metricData.data.find(i => i["dimension-values"].indexOf(dimension.dimension) !== -1);

    if (!dataItem) {
        return "-";
    }

    return metric.isRate
        ? `${(dataItem["metric-value"] * 100).toFixed(2)}%`
        : `${formatNumber(dataItem["metric-value"])}`;
}

function calculateDimensionPercents(
    data: ChartDataResponse,
    metric: MetricInfo,
    dimensions: DimensionInfo[]
): Array<{ dimension: string; percent: number }> {
    const percentMetricData = data.metrics.find(m => m.metric === metric.metric);

    const vals = dimensions.map(dimension => ({
        dimension: dimension.dimension,
        value: percentMetricData
            ? (percentMetricData.data.find(d => d["dimension-values"].indexOf(dimension.dimension) !== -1) || {
                  "metric-value": 0
              })["metric-value"]
            : 0
    }));

    const total = vals.reduce((sum, item) => sum + item.value, 0);

    return vals.map(i => ({
        dimension: i.dimension,
        percent: (i.value / Math.max(total, 1)) * 100
    }));
}
